import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { remountBackInStockModalsFromHtml } from '@theme/back-in-stock';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowOptions } from '@theme/overflow-options';
import { yieldToMainThread, getViewParameterValue, normalizeOptionValueKey, debounce } from '@theme/utilities';

/**
 * Reads the `script[data-options-block-variants]` variants list rendered alongside the picker
 * (emitted for stack mode, and for any picker with an ERP-masked option).
 * @param {Element | null | undefined} form
 * @returns {Array<Record<string, unknown>>}
 */
function readOptionsBlockVariants(form) {
  const script = form?.querySelector('script[data-options-block-variants]');
  if (!script?.textContent) return [];
  try {
    const parsed = JSON.parse(script.textContent);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} v
 * @returns {string[]}
 */
function variantOptionsArray(v) {
  if (Array.isArray(v?.options) && v.options.length) return /** @type {string[]} */ (v.options);
  const o = [];
  for (const k of /** @type {const} */ (['option1', 'option2', 'option3'])) {
    const val = v?.[k];
    if (val == null || val === '') break;
    o.push(String(val));
  }
  return o;
}

/**
 * Derives the real (ERP) option value paired with `want` on the same variant, so a masked ERP
 * option (e.g. 'Color' = 'NAV STRP') can be selected from a display-only option's value (e.g.
 * 'Colors' = 'Navy Stripe') even when the two names don't share any normalizable spelling.
 *
 * WHY THIS EXISTS (do not remove / replace with pure name-matching): `normalizeOptionValueKey`
 * can only bridge cosmetic differences (spacing, punctuation, case, slashes). It cannot bridge a
 * true abbreviation like 'NAV STRP' for 'Navy Stripe' — there's no reliable algorithm to derive
 * one from the other. Instead of guessing from the text, this reads the real pairing straight out
 * of Shopify's own variant data: every variant of a combined-listing product that masks a native
 * "Color" option behind a "Colors" swatch option already stores BOTH values together on the same
 * record (e.g. `options: ["Navy Stripe", "NAV STRP", "M"]`). We find the variant whose display
 * option equals `want` and read the ERP value off that exact record — a data lookup/join, not a
 * text-matching heuristic. This needs no metafield/metaobject and no merchant maintenance; it
 * automatically works for any abbreviation as long as the product's variants carry both option
 * values together (which is the existing "ERP masked option" data model — see
 * util-combined-listing-erp-mask.liquid). Kept in sync with the near-identical copy of this
 * function in options-block-stack.js (that one drives product-card "stack" quick add; this one
 * drives the standard/PDP variant picker) — if you fix a bug here, fix it there too.
 * @param {Array<Record<string, unknown>>} variants
 * @param {number} fromIdx
 * @param {number} maskedIdx
 * @param {string} want
 * @returns {string | null}
 */
function deriveMaskedOptionValue(variants, fromIdx, maskedIdx, want) {
  const wantKey = normalizeOptionValueKey(want);
  if (!wantKey) return null;
  // Legacy/ghost option values can linger on old, unavailable variants (e.g. a combined-listing
  // member that used to sell other colorways under the same 'Colors' value, left over from before
  // it was split into separate listing products). Without this availability preference, we could
  // derive a stale/wrong ERP value from a dead variant instead of the real one, which made Size
  // (and other dependent options) incorrectly show as unavailable. Prefer an `available: true`
  // match first; only fall back to any match if nothing available was found.
  let fallback = null;
  for (const v of variants) {
    const opts = variantOptionsArray(v);
    if (opts.length <= fromIdx || opts.length <= maskedIdx) continue;
    if (normalizeOptionValueKey(opts[fromIdx]) !== wantKey) continue;
    const target = optStr(opts[maskedIdx]);
    if (!target) continue;
    if (v?.available === true) return target;
    if (fallback == null) fallback = target;
  }
  return fallback;
}

/**
 * @param {string | null | undefined} s
 * @returns {string}
 */
function optStr(s) {
  return s == null ? '' : String(s).trim();
}

/**
 * @param {string | null | undefined} a
 * @param {string | null | undefined} b
 * @returns {boolean}
 */
function sameOptionKey(a, b) {
  return normalizeOptionValueKey(a) === normalizeOptionValueKey(b);
}

/**
 * @param {string | null | undefined} raw - A `data-input-id` value like "3-0".
 * @returns {number} Zero-based option position, or -1.
 */
function slotPosFromInputId(raw) {
  if (!raw) return -1;
  const p = Number.parseInt(String(raw).split('-')[0] || '0', 10) - 1;
  return Number.isNaN(p) ? -1 : p;
}

/**
 * @param {Array<Record<string, unknown>>} variants
 * @returns {number}
 */
function maxVariantOptionCount(variants) {
  let n = 0;
  for (const v of variants) {
    const len = variantOptionsArray(v).length;
    if (len > n) n = len;
  }
  return n;
}

/**
 * Whether an available variant exists whose option at `pos` equals `value` while agreeing with the
 * other currently-selected `slots`. Mirrors the stack picker's constrained-availability check so
 * the standard picker can recompute availability client-side for ERP-masked combined listings.
 * @param {Array<Record<string, unknown>>} variants
 * @param {number} pos
 * @param {string} value
 * @param {Array<string|undefined>} slots
 * @param {number} n
 * @returns {boolean}
 */
function hasAvailableConstrainedVariant(variants, pos, value, slots, n) {
  const want = optStr(value);
  if (!want) return false;
  for (const v of variants) {
    if (!v || v.available !== true) continue;
    const opts = variantOptionsArray(v);
    if (opts.length < n) continue;
    if (!sameOptionKey(opts[pos], want)) continue;
    let ok = true;
    for (let j = 0; j < n; j++) {
      if (j === pos) continue;
      const s = slots[j];
      if (s == null || String(s).trim() === '') continue;
      if (!sameOptionKey(opts[j], s)) {
        ok = false;
        break;
      }
    }
    if (ok) return true;
  }
  return false;
}

/**
 * @param {Node | undefined} node
 * @returns {string | undefined}
 */
function variantPickerMorphNodeKey(node) {
  if (!(node instanceof HTMLElement)) return undefined;
  const optionValueId = node.dataset.optionValueId?.trim();
  if (optionValueId) return optionValueId;
  const key = node.dataset.key?.trim();
  if (key && key !== 'variant-option-text') return key;
  return undefined;
}

/**
 * @param {HTMLElement} selectedOption
 * @returns {string | undefined}
 */
function variantIdForCardRequest(selectedOption) {
  if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
    return undefined;
  }
  const fromDataset = selectedOption.dataset.variantId?.trim();
  if (fromDataset) return fromDataset;
  const connected = (selectedOption.dataset.connectedProductUrl || '').trim();
  if (!connected) return undefined;
  try {
    const fromUrl = new URL(connected, window.location.origin).searchParams.get('variant')?.trim();
    if (fromUrl) return fromUrl;
  } catch {
    // fall through
  }
  return undefined;
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** Global semaphore: max concurrent card section fetches across all cards to avoid 429s */
  static #cardFetchActive = 0;
  static #cardFetchConcurrency = 2;
  /** @type {Array<{ picker: object; run: () => void }>} */
  static #cardFetchQueue = [];

  static #drainCardFetchQueue() {
    while (VariantPicker.#cardFetchActive < VariantPicker.#cardFetchConcurrency && VariantPicker.#cardFetchQueue.length > 0) {
      const next = VariantPicker.#cardFetchQueue.shift();
      if (next) {
        VariantPicker.#cardFetchActive++;
        next.run();
      }
    }
  }

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    this.#syncFieldsetRadios();
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  #syncFieldsetRadios() {
    this.#radios = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);
    });

    this.#recomputeMaskedOptionAvailability();
  }

  /**
   * @param {Element} maskedEl
   * @returns {number | null}
   */
  #maskedErpOptionSlotIndex(maskedEl) {
    const fromAttr = Number.parseInt(maskedEl.getAttribute('data-fieldset-index') || '', 10);
    if (!Number.isNaN(fromAttr)) return fromAttr;

    const sample =
      maskedEl.querySelector('input[type="radio"][data-input-id]') ??
      maskedEl.querySelector('select.variant-option__select option[data-input-id]');
    const raw =
      sample instanceof HTMLInputElement
        ? sample.dataset.inputId
        : sample instanceof HTMLOptionElement
          ? sample.getAttribute('data-input-id')
          : null;
    if (!raw) return null;

    const idx = Number.parseInt(String(raw).split('-')[0] || '0', 10) - 1;
    return Number.isNaN(idx) ? null : idx;
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {string} want
   * @returns {boolean}
   */
  #syncMaskedErpSelect(select, want) {
    const wantKey = normalizeOptionValueKey(want);
    let matched = null;
    for (const option of select.options) {
      if (normalizeOptionValueKey(option.value) === wantKey) {
        matched = option;
        break;
      }
    }
    if (!matched) return false;

    for (const option of select.options) {
      option.removeAttribute('selected');
    }
    matched.setAttribute('selected', 'selected');
    select.value = matched.value;
    return true;
  }

  #syncErpMaskedOptionsForRequest() {
    const form = this.querySelector('form.variant-picker__form');
    if (!form) return;

    /** @type {Array<string | undefined>} */
    const slots = [];
    for (const fieldset of form.querySelectorAll('fieldset[data-fieldset-index]')) {
      const idx = Number.parseInt(fieldset.getAttribute('data-fieldset-index') || '', 10);
      if (Number.isNaN(idx)) continue;
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement) {
        slots[idx] = checked.value;
      }
    }
    for (const select of form.querySelectorAll('select.variant-option__select')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const opt = select.options[select.selectedIndex];
      if (!opt) continue;
      const raw = opt.getAttribute('data-input-id');
      if (!raw) continue;
      const idx = Number.parseInt(String(raw).split('-')[0] || '0', 10) - 1;
      if (Number.isNaN(idx) || idx < 0) continue;
      const val = String(opt.value).trim();
      if (val) slots[idx] = opt.value;
    }
    if (!slots.some((slot) => slot != null && String(slot).trim() !== '')) return;

    const variants = readOptionsBlockVariants(form);

    for (const maskedEl of form.querySelectorAll(
      '[data-erp-masked-option="true"][data-erp-sync-from-fieldset-index]'
    )) {
      const fromIdx = Number.parseInt(maskedEl.getAttribute('data-erp-sync-from-fieldset-index') || '', 10);
      if (Number.isNaN(fromIdx) || fromIdx < 0 || fromIdx >= slots.length) continue;

      const fromValue = slots[fromIdx];
      if (fromValue == null || String(fromValue).trim() === '') continue;
      const want = String(fromValue).trim();

      const maskedIdx = this.#maskedErpOptionSlotIndex(maskedEl);
      if (maskedIdx == null || maskedIdx < 0 || maskedIdx >= slots.length) continue;

      const effectiveWant = deriveMaskedOptionValue(variants, fromIdx, maskedIdx, want) ?? want;

      const maskedSelect = maskedEl.querySelector('select.variant-option__select');
      if (maskedSelect instanceof HTMLSelectElement) {
        if (this.#syncMaskedErpSelect(maskedSelect, effectiveWant)) {
          slots[maskedIdx] = maskedSelect.value;
        }
        continue;
      }

      const wantKey = normalizeOptionValueKey(effectiveWant);
      for (const input of maskedEl.querySelectorAll('input[type="radio"]')) {
        if (!(input instanceof HTMLInputElement)) continue;
        if (normalizeOptionValueKey(input.value) === wantKey) {
          input.checked = true;
          input.dataset.currentChecked = 'true';
          slots[maskedIdx] = input.value;
        } else if (input.checked) {
          input.checked = false;
          input.dataset.currentChecked = 'false';
        }
      }
    }
  }

  /**
   * Reads the currently-selected option values by position, filling masked ERP slots with the
   * value derived from the paired variant (so e.g. the hidden 'Color' slot resolves to 'NAV STRP'
   * from the visible 'Colors' = 'Navy Stripe' selection).
   * @param {Element} form
   * @param {Array<Record<string, unknown>>} variants
   * @returns {Array<string | undefined>}
   */
  #collectSelectedSlots(form, variants) {
    /** @type {Array<string | undefined>} */
    const slots = [];
    for (const fieldset of form.querySelectorAll('fieldset[data-fieldset-index]')) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!(checked instanceof HTMLInputElement)) continue;
      const pos = slotPosFromInputId(checked.dataset.inputId);
      if (pos >= 0) slots[pos] = checked.value;
    }
    for (const select of form.querySelectorAll('select.variant-option__select')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const opt = select.options[select.selectedIndex];
      if (!opt) continue;
      const pos = slotPosFromInputId(opt.getAttribute('data-input-id'));
      if (pos >= 0 && optStr(opt.value)) slots[pos] = opt.value;
    }
    for (const maskedEl of form.querySelectorAll(
      '[data-erp-masked-option="true"][data-erp-sync-from-fieldset-index]'
    )) {
      const fromIdx = Number.parseInt(maskedEl.getAttribute('data-erp-sync-from-fieldset-index') || '', 10);
      const maskedIdx = this.#maskedErpOptionSlotIndex(maskedEl);
      if (Number.isNaN(fromIdx) || maskedIdx == null || maskedIdx < 0) continue;
      const fromValue = slots[fromIdx];
      if (fromValue == null || optStr(fromValue) === '') continue;
      const derived = deriveMaskedOptionValue(variants, fromIdx, maskedIdx, optStr(fromValue));
      if (derived) slots[maskedIdx] = derived;
    }
    return slots;
  }

  /**
   * Recomputes option-value availability client-side for ERP-masked combined listings, where the
   * server renders dependent options (e.g. Size) as unavailable because the visible 'Colors' value
   * doesn't literally match the ERP 'Color' variant value. No-op for normal products: it only runs
   * when the picker emits a variants list and has a masked option, and never for stack/card forms
   * (those are handled by options-block-stack.js). Combined-listing color options are left to the
   * server (their sibling values point to other products not present in this variants list).
   *
   * WHY THIS EXISTS: unlike the product-card "stack" picker (options-block-stack.js), the
   * standard/PDP variant picker previously had NO client-side availability logic at all — it only
   * ever displayed whatever Shopify/Liquid rendered server-side. For ERP-masked combined-listing
   * products, that server-rendered availability can be wrong (see `deriveMaskedOptionValue`'s doc
   * comment for why), so every Size button rendered as unavailable even when in stock. This method
   * mirrors the stack picker's availability recompute so the PDP behaves the same way. If you find
   * a bug in this method, check whether the same bug exists in options-block-stack.js's
   * `updateConstrainedOptionAvailability`/`hasAvailableConstrainedOption`, and vice versa.
   */
  #recomputeMaskedOptionAvailability() {
    const form = this.querySelector('form.variant-picker__form');
    if (!form || form.classList.contains('options-block--stack-form')) {
      return;
    }
    if (!form.querySelector('[data-erp-masked-option="true"]')) {
      return;
    }

    const variants = readOptionsBlockVariants(form);
    if (!variants.length) return;
    const n = maxVariantOptionCount(variants);
    if (n < 1) return;

    const slots = this.#collectSelectedSlots(form, variants);

    for (const fieldset of form.querySelectorAll('fieldset[data-fieldset-index]')) {
      if (fieldset.matches('[data-erp-masked-option="true"]')) continue;
      // Combined-listing color options navigate to other products; their availability is server-owned.
      if (fieldset.querySelector('input[data-connected-product-url]:not([data-connected-product-url=""])')) {
        continue;
      }
      for (const input of fieldset.querySelectorAll('input[type="radio"][data-input-id]')) {
        if (!(input instanceof HTMLInputElement)) continue;
        const pos = slotPosFromInputId(input.dataset.inputId);
        if (pos < 0 || pos >= n) continue;
        const canBuy = hasAvailableConstrainedVariant(variants, pos, input.value, slots, n);
        input.setAttribute('data-option-available', canBuy ? 'true' : 'false');
        if (canBuy) {
          input.removeAttribute('aria-disabled');
        } else {
          input.setAttribute('aria-disabled', 'true');
        }
      }
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const inStackRadio =
      event.target instanceof HTMLInputElement &&
      event.target.type === 'radio' &&
      Boolean(this.querySelector('form.options-block--stack-form')?.contains(event.target));

    if (inStackRadio) {
      this.updateSelectedOption(event.target);
      this.dispatchEvent(
        new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' })
      );
      const connected = (selectedOption.dataset.connectedProductUrl || '').trim();
      const onProductCard = Boolean(this.closest('product-card'));
      // options-block-stack owns card stack matching, auto-add, and section fetch.
      if (onProductCard && !connected) {
        return;
      }
      // Stack radios off cards skip the section fetch; PDP still needs it for the media gallery.
      if (!connected && !onProductCard && !isOnProductPage) {
        return;
      }
    } else {
      this.updateSelectedOption(event.target);
      this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));
    }

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, {
      isOnProductPage,
      loadsNewProduct,
      connectedProductUrl: newUrl,
      sourceOptionValueId: selectedOption.dataset.optionValueId ?? '',
    });
  }

  /**
   * @param {{ isOnProductPage?: boolean, loadsNewProduct?: boolean, connectedProductUrl?: string }} context
   * @param {{ id?: string | number } | null | undefined} resource
   */
  #syncProductPageHistory(context, resource) {
    if (!context.isOnProductPage || resource?.id == null) return;

    const url = new URL(window.location.href);

    if (context.loadsNewProduct && context.connectedProductUrl) {
      url.pathname = new URL(context.connectedProductUrl, window.location.origin).pathname;
    }

    url.searchParams.set('variant', String(resource.id));

    if (url.href === window.location.href) return;

    yieldToMainThread().then(() => {
      history.replaceState({}, '', url.toString());
    });
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const esc =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(target) : target;
      const targetElement = this.querySelector(`[data-option-value-id="${esc}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const radios = this.#radios[fieldsetIndex];

      target.checked = true;

      if (!Number.isNaN(fieldsetIndex) && radios) {
        for (const radio of radios) {
          if (radio instanceof HTMLInputElement) {
            radio.dataset.currentChecked = radio.checked ? 'true' : 'false';
          }
        }
      }
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    const connectedUrl =
      selectedOption instanceof HTMLElement ? (selectedOption.dataset.connectedProductUrl || '').trim() : '';
    let productUrl = connectedUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    const productCard = this.closest('product-card');
    const inQuickAddDialog = Boolean(this.closest('quick-add-dialog'));
    const resolvedVariantId = variantIdForCardRequest(selectedOption);
    const useResolvedVariantParam =
      (productCard || inQuickAddDialog) && connectedUrl && resolvedVariantId && !source;
    const skipOptionValuesForConnectedListing =
      Boolean(connectedUrl) && (productCard || inQuickAddDialog) && !source;

    if (useResolvedVariantParam) {
      params.push(`variant=${resolvedVariantId}`);
    } else if (!skipOptionValuesForConnectedListing && this.selectedOptionsValuesForRequest.length && !source) {
      params.push(`option_values=${this.selectedOptionsValuesForRequest.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const hasOptionValues = params.some((p) => p.startsWith('option_values='));
    if (
      resolvedVariantId &&
      !hasOptionValues &&
      !params.some((p) => p.startsWith('variant='))
    ) {
      const allowVariantFallback = connectedUrl
        ? productCard || inQuickAddDialog
        : productCard && !inQuickAddDialog;
      if (allowVariantFallback) {
        params.push(`variant=${resolvedVariantId}`);
      }
    }

    const featuredSection = this.closest('featured-product-information');
    if (featuredSection instanceof HTMLElement && featuredSection.id) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${featuredSection.id}&${params.join('&')}`;
    }

    if (this.closest('quick-add-dialog')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      const sectionId = this.dataset.sectionId;
      if (sectionId) {
        return `${productUrl}?section_id=${sectionId}&${params.join('&')}`;
      }
      return `${productUrl}?${params.join('&')}`;
    }

    if (this.closest('quick-add-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    if (productCard) {
      const baseFromPicker = this.dataset.productUrl?.split('?')[0];
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      if (baseFromPicker && !connectedUrl) {
        productUrl = baseFromPicker;
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @param {Document | Element} newHtml
   * @returns {Element | null}
   */
  #selectPickerSourceFromResponse(newHtml) {
    const own = newHtml.querySelector(this.tagName.toLowerCase());
    if (own) return own;
    if (this.tagName === 'SWATCHES-VARIANT-PICKER-COMPONENT') {
      // Fall back to the main variant-picker emitted by section-rendering-
      // product-card. A second swatches-variant-picker-component lookup is
      // unreachable because the own-tag query above already ruled it out.
      return newHtml.querySelector('variant-picker');
    }
    return null;
  }

  /** @type {((requestUrl: string, morphElementSelector: string | undefined, historyContext: { isOnProductPage?: boolean, loadsNewProduct?: boolean, connectedProductUrl?: string, sourceOptionValueId?: string }) => void) | null} */
  #debouncedFetchUpdatedSection = null;

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ isOnProductPage?: boolean, loadsNewProduct?: boolean, connectedProductUrl?: string, sourceOptionValueId?: string }} [historyContext]
   */
  fetchUpdatedSection(/** @type {string} */ requestUrl, /** @type {string | undefined} */ morphElementSelector, historyContext = {}) {
    if (this.closest('product-card')) {
      if (!this.#debouncedFetchUpdatedSection) {
        this.#debouncedFetchUpdatedSection = debounce(
          (url, selector, ctx) => this.#doFetchUpdatedSection(url, selector, ctx),
          300
        );
      }
      this.#debouncedFetchUpdatedSection(requestUrl, morphElementSelector, historyContext);
      return;
    }
    this.#doFetchUpdatedSection(requestUrl, morphElementSelector, historyContext);
  }

  /**
   * @param {string} requestUrl
   * @param {string | undefined} morphElementSelector
   * @param {{ isOnProductPage?: boolean, loadsNewProduct?: boolean, connectedProductUrl?: string, sourceOptionValueId?: string }} [historyContext]
   */
  #doFetchUpdatedSection(requestUrl, morphElementSelector, historyContext = {}) {
    const onCard = Boolean(this.closest('product-card'));
    if (onCard) {
      // Abort any pending fetch from this picker and drop its stale queued entry before queuing a new one
      this.#abortController?.abort();
      VariantPicker.#cardFetchQueue = VariantPicker.#cardFetchQueue.filter((t) => t.picker !== this);
      VariantPicker.#cardFetchQueue.push({ picker: this, run: () => this.#runCardSectionFetch(requestUrl, morphElementSelector, historyContext, true) });
      VariantPicker.#drainCardFetchQueue();
      return;
    }
    this.#runCardSectionFetch(requestUrl, morphElementSelector, historyContext);
  }

  /**
   * @param {string} requestUrl
   * @param {string | undefined} morphElementSelector
   * @param {{ isOnProductPage?: boolean, loadsNewProduct?: boolean, connectedProductUrl?: string, sourceOptionValueId?: string }} [historyContext]
   * @param {boolean} [releaseSemaphore]
   */
  #runCardSectionFetch(requestUrl, morphElementSelector, historyContext = {}, releaseSemaphore = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController?.signal;

    const cardForSectionFetch = this.closest('product-card');
    if (cardForSectionFetch instanceof HTMLElement) {
      cardForSectionFetch.setAttribute('data-card-variant-section-fetch', '');
    }

    const fetchWithRetry = async (/** @type {string} */ url, /** @type {AbortSignal} */ sig) => {
      const maxRetries = 3;
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (sig.aborted) throw new DOMException('Aborted', 'AbortError');
        const res = await fetch(url, { signal: sig });
        if (res.status === 429 && attempt < maxRetries - 1) {
          const retryAfterMs = Number(res.headers.get('Retry-After') ?? 0) * 1000 || (1000 * 2 ** attempt);
          await new Promise((resolve) => setTimeout(resolve, retryAfterMs));
          continue;
        }
        return res;
      }
      throw new Error('fetchWithRetry exhausted');
    };

    fetchWithRetry(requestUrl, signal)
      .then(async (response) => {
        this.#pendingRequestUrl = undefined;

        /** @type {string} */
        let responseText = await response.text();
        const cardOptionsBlockId = this.dataset.blockId;
        /** @param {string} text */
        const applyCardOptionsBlockId = (text) => {
          if (cardOptionsBlockId && text.includes('__CARD_OPTIONS_BLOCK_ID__')) {
            return text.split('__CARD_OPTIONS_BLOCK_ID__').join(cardOptionsBlockId);
          }
          return text;
        };
        responseText = applyCardOptionsBlockId(responseText);
        /** @type {boolean} */
        let ok = response.ok;

        if (!ok && !signal.aborted) {
          const url = new URL(requestUrl, window.location.href);
          if (url.searchParams.has('variant')) {
            url.searchParams.delete('variant');
            // Use a fresh controller for the retry so a subsequent
            // variantChanged that replaces this.#abortController doesn't
            // silently abort the recovery fetch and drop the response.
            const retryController = new AbortController();
            const onParentAbort = () => retryController.abort();
            signal.addEventListener('abort', onParentAbort, { once: true });
            try {
              const second = await fetch(url.toString(), { signal: retryController.signal });
              responseText = await second.text();
              responseText = applyCardOptionsBlockId(responseText);
              ok = second.ok;
            } finally {
              signal.removeEventListener('abort', onParentAbort);
            }
          }
        }

        if (!ok) {
          console.warn('Section rendering failed after retries', requestUrl);
          return;
        }

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const selectedVariantScript =
          html.querySelector(
            'variant-picker form script[type="application/json"]:not([data-options-block-variants])'
          ) ?? html.querySelector('variant-picker form script[type="application/json"]');
        const textContent = selectedVariantScript?.textContent;
        if (!textContent) {
          return;
        }

        if (!this.#selectPickerSourceFromResponse(html)) {
          console.warn('Section response missing', this.tagName, requestUrl);
          return;
        }

        if (morphElementSelector === 'main') {
          // Morph moves nodes out of the parsed document; keep a clone for variant:update
          // listeners (media-gallery replaceWith, sticky-add-to-cart, etc.).
          const htmlForVariantUpdate = /** @type {Document} */ (html.cloneNode(true));

          this.updateMain(html);
          this.#syncFieldsetRadios();

          let sourceId;
          try {
            sourceId = this.selectedOptionId;
          } catch {
            sourceId = historyContext.sourceOptionValueId;
          }
          if (!sourceId) return;

          let resource;
          try {
            resource = JSON.parse(textContent);
          } catch {
            return;
          }

          const variantPickerInResponse = htmlForVariantUpdate.querySelector('variant-picker');
          const newProduct =
            variantPickerInResponse instanceof HTMLElement &&
            variantPickerInResponse.dataset.productId &&
            variantPickerInResponse.dataset.productUrl
              ? {
                  id: variantPickerInResponse.dataset.productId,
                  url: variantPickerInResponse.dataset.productUrl,
                }
              : undefined;

          this.dispatchEvent(
            new VariantUpdateEvent(resource, sourceId, {
              html: htmlForVariantUpdate,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
          this.#syncProductPageHistory(historyContext, resource);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);
          if (newProduct === null) {
            return;
          }

          let sourceId;
          try {
            sourceId = this.selectedOptionId;
          } catch {
            sourceId = historyContext.sourceOptionValueId;
          }

          if (sourceId) {
            let resource;
            try {
              resource = JSON.parse(textContent);
            } catch {
              return;
            }
            this.dispatchEvent(
              new VariantUpdateEvent(resource, sourceId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
            this.#syncProductPageHistory(historyContext, resource);
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        if (cardForSectionFetch instanceof HTMLElement) {
          cardForSectionFetch.removeAttribute('data-card-variant-section-fetch');
        }
        if (releaseSemaphore) {
          VariantPicker.#cardFetchActive = Math.max(0, VariantPicker.#cardFetchActive - 1);
          VariantPicker.#drainCardFetchQueue();
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined | null} newProduct if morphed; undefined if morphed with same product; null if morph skipped.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = this.#selectPickerSourceFromResponse(newHtml);

    if (!newVariantPickerSource) {
      console.warn('No new variant picker source found');
      return null;
    }

    if (this.tagName === 'SWATCHES-VARIANT-PICKER-COMPONENT' && newVariantPickerSource instanceof HTMLElement) {
      if (newVariantPickerSource.tagName === 'VARIANT-PICKER') {
        const newProductId = newVariantPickerSource.dataset.productId;
        const newProductUrl = newVariantPickerSource.dataset.productUrl;
        if (newProductId && this.dataset.productId !== newProductId) {
          newProduct = { id: newProductId, url: newProductUrl ?? '' };
        }
        if (newProductId) this.dataset.productId = newProductId;
        if (newProductUrl) this.dataset.productUrl = newProductUrl;
        this.#syncFieldsetRadios();
        return newProduct;
      }
    }

    if (
      this.tagName === 'VARIANT-PICKER' &&
      this.querySelector('form.options-block--stack-form') &&
      !this.closest('quick-add-dialog')
    ) {
      if (newVariantPickerSource instanceof HTMLElement) {
        const newStackVariants = newVariantPickerSource.querySelector('script[data-options-block-variants]');
        if (newStackVariants?.textContent) {
          const oldStackVariants = this.querySelector('script[data-options-block-variants]');
          if (oldStackVariants) {
            oldStackVariants.textContent = newStackVariants.textContent;
          }
          const newProductId = newVariantPickerSource.dataset.productId;
          const newProductUrl = newVariantPickerSource.dataset.productUrl;
          if (newProductId && this.dataset.productId !== newProductId) {
            newProduct = { id: newProductId, url: newProductUrl ?? '' };
          }
          if (newProductId) this.dataset.productId = newProductId;
          if (newProductUrl) this.dataset.productUrl = newProductUrl;

          const newSelected = newVariantPickerSource.querySelector(
            'form script[type="application/json"]:not([data-options-block-variants])'
          );
          const oldSelected = this.querySelector(
            'form script[type="application/json"]:not([data-options-block-variants])'
          );
          if (newSelected && oldSelected) {
            oldSelected.textContent = newSelected.textContent;
          }
          this.#syncFieldsetRadios();
          return newProduct;
        }
      }
    }

    const liveHasMainForm = Boolean(this.querySelector('form.variant-picker__form'));
    const sourceIsSectionStub =
      newVariantPickerSource instanceof HTMLElement &&
      !newVariantPickerSource.querySelector('form.variant-picker__form') &&
      Boolean(newVariantPickerSource.querySelector('script[type="application/json"]'));

    if (this.tagName === 'VARIANT-PICKER' && liveHasMainForm && sourceIsSectionStub) {
      if (newVariantPickerSource instanceof HTMLElement) {
        const newProductId = newVariantPickerSource.dataset.productId;
        const newProductUrl = newVariantPickerSource.dataset.productUrl;

        if (newProductId && this.dataset.productId !== newProductId) {
          newProduct = { id: newProductId, url: newProductUrl ?? '' };
        }

        if (newProductId) this.dataset.productId = newProductId;
        if (newProductUrl) this.dataset.productUrl = newProductUrl;

        const newScript = newVariantPickerSource.querySelector('script[type="application/json"]');
        const oldScript = this.querySelector('script[type="application/json"]');
        if (newScript && oldScript) {
          oldScript.textContent = newScript.textContent;
        }
      }
      return newProduct;
    }

    const previousProductId = this.dataset.productId;

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && previousProductId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl ?? '' };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const liveOverflowOptions = this.querySelector('overflow-options');
      const desktopLimit = liveOverflowOptions?.getAttribute('desktop-limit');
      const mobileLimit = liveOverflowOptions?.getAttribute('mobile-limit');

      if (desktopLimit || mobileLimit) {
        for (const el of newVariantPickerSource.querySelectorAll('overflow-options')) {
          if (desktopLimit) el.setAttribute('desktop-limit', desktopLimit);
          if (mobileLimit) el.setAttribute('mobile-limit', mobileLimit);
        }
      }
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: variantPickerMorphNodeKey,
    });

    this.#syncFieldsetRadios();

    queueMicrotask(() => {
      for (const el of this.querySelectorAll('overflow-options')) {
        if (el instanceof OverflowOptions) el.refresh();
      }
    });

    return newProduct;
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
    remountBackInStockModalsFromHtml(main, newHtml);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset:not([data-erp-masked-option="true"]) input:checked'
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset:not([data-erp-masked-option="true"]) input:checked'
      )
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  get selectedOptionsValuesForRequest() {
    this.#syncErpMaskedOptionsForRequest();

    /** @type {HTMLElement[]} */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input[type="radio"]:checked')
    );

    /** @param {HTMLElement} el */
    const optionSortKey = (el) => {
      const fromFieldset = el.dataset.fieldsetIndex;
      if (fromFieldset != null && fromFieldset !== '') {
        const n = Number.parseInt(fromFieldset, 10);
        if (!Number.isNaN(n)) return n;
      }
      const inputId = el.dataset.inputId || el.getAttribute('data-input-id');
      if (inputId) {
        const n = Number.parseInt(String(inputId).split('-')[0] || '0', 10) - 1;
        if (!Number.isNaN(n)) return n;
      }
      return 0;
    };

    selectedOptions.sort((a, b) => optionSortKey(a) - optionSortKey(b));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * @param {Event} [event]
   */
  showAllSwatches(event) {
    event?.preventDefault();
    /** @type {Element | null} */
    let target = null;
    if (event?.target instanceof Element) {
      target = event.target.closest('overflow-options');
    }
    // Fallback to this picker's own overflow-options descendant; never reach
    // through a legacy refs.overflowList alias which may point at a different
    // component in the swatches block.
    if (!target) target = this.querySelector('overflow-options');
    if (
      target instanceof HTMLElement &&
      'showAll' in target &&
      typeof /** @type {{ showAll?: () => void }} */ (target).showAll === 'function'
    ) {
      /** @type {{ showAll: () => void }} */ (target).showAll();
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
